// frontend/src/features/wind/WindProjectListPanel.js
import React, { useMemo, useState, useEffect } from "react";
import { MiniProjectCard } from "./ui";

/**
 * Project list with search, sort, and pagination (25 per page).
 */
const PAGE_SIZE = 25;

export default function WindProjectListPanel({
  projects,
  loading,
  selectedIds,
  selectedProject,
  onToggleSelected,
  onClearSelection,
  onProjectClick,
  onStartCompSet,
  canStartCompSet,
}) {
  const [search, setSearch] = useState("");
  const [sortMode, setSortMode] = useState("capacity-desc"); // name-asc | capacity-desc | capacity-asc | year-desc | year-asc
  const [page, setPage] = useState(0);

  // Reset page when the underlying data changes
  useEffect(() => {
    setPage(0);
  }, [projects.length, search, sortMode]);

  const filteredSorted = useMemo(() => {
    const q = search.trim().toLowerCase();

    let filtered = projects;
    if (q) {
      filtered = projects.filter((p) => {
        const name = (p.name || "").toLowerCase();
        const state = (p.state || "").toLowerCase();
        const counties = Array.isArray(p.counties)
          ? p.counties.join(" ").toLowerCase()
          : "";
        const year =
          typeof p.year === "number" || typeof p.year === "string"
            ? String(p.year)
            : "";
        return (
          name.includes(q) ||
          state.includes(q) ||
          counties.includes(q) ||
          year.includes(q)
        );
      });
    }

    const arr = [...filtered];
    arr.sort((a, b) => {
      const capA =
        typeof a.capacity_mw === "number" ? Number(a.capacity_mw) : 0;
      const capB =
        typeof b.capacity_mw === "number" ? Number(b.capacity_mw) : 0;
      const yearA =
        typeof a.year === "number" || typeof a.year === "string"
          ? parseInt(a.year, 10)
          : 0;
      const yearB =
        typeof b.year === "number" || typeof b.year === "string"
          ? parseInt(b.year, 10)
          : 0;
      const nameA = (a.name || "").toLowerCase();
      const nameB = (b.name || "").toLowerCase();

      switch (sortMode) {
        case "name-asc":
          return nameA.localeCompare(nameB);
        case "capacity-asc":
          return capA - capB;
        case "capacity-desc":
          return capB - capA;
        case "year-asc":
          return yearA - yearB;
        case "year-desc":
          return yearB - yearA;
        default:
          return 0;
      }
    });

    return arr;
  }, [projects, search, sortMode]);

  const total = filteredSorted.length;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);

  const pageItems = useMemo(() => {
    const start = currentPage * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    return filteredSorted.slice(start, end);
  }, [filteredSorted, currentPage]);

  const handlePrev = () =>
    setPage((p) => (p > 0 ? p - 1 : p));
  const handleNext = () =>
    setPage((p) => (p < pageCount - 1 ? p + 1 : p));

  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/70 px-4 py-4">
      <div className="flex items-start justify-between mb-2 gap-2">
        <div>
          <div className="text-xs font-semibold text-slate-200">
            Project list
          </div>
          <div className="text-[11px] text-slate-400">
            All wind farms in the current view. Search, sort, and select
            projects to build a Comp Set.
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, state, county, year…"
            className="rounded-lg border border-slate-700 bg-slate-950/80 px-2.5 py-1 text-[11px] text-slate-100 w-48"
          />
          <div className="text-[10px] text-slate-500 whitespace-nowrap">
            {loading
              ? "Loading…"
              : `${total.toLocaleString()} project${
                  total === 1 ? "" : "s"
                } in results`}
          </div>
        </div>
      </div>

      {/* Selection & sort controls */}
      <div className="flex flex-wrap items-center justify-between mb-2 gap-3">
        <div className="flex items-center gap-2 text-[11px] text-slate-400">
          <span>
            {selectedIds.length
              ? `${selectedIds.length} selected`
              : "No projects selected"}
          </span>
          <button
            type="button"
            onClick={onClearSelection}
            disabled={!selectedIds.length}
            className="rounded-lg border border-slate-700 bg-slate-950/70 px-2.5 py-1 text-[11px] text-slate-200 disabled:opacity-40 hover:bg-slate-900/90 transition"
          >
            Clear
          </button>
        </div>

        <div className="flex items-center gap-3 text-[11px]">
          <div className="flex items-center gap-1">
            <span className="text-slate-400">Sort by</span>
            <select
              value={sortMode}
              onChange={(e) => setSortMode(e.target.value)}
              className="rounded-lg border border-slate-700 bg-slate-950/80 px-2 py-1 text-[11px] text-slate-100"
            >
              <option value="capacity-desc">Capacity (high → low)</option>
              <option value="capacity-asc">Capacity (low → high)</option>
              <option value="year-desc">Vintage (new → old)</option>
              <option value="year-asc">Vintage (old → new)</option>
              <option value="name-asc">Name (A → Z)</option>
            </select>
          </div>
          <button
            type="button"
            onClick={onStartCompSet}
            disabled={!canStartCompSet}
            className="rounded-lg border border-emerald-500/60 bg-emerald-900/20 px-3 py-1 text-[11px] text-emerald-100 disabled:opacity-40 hover:bg-emerald-900/40 transition"
            title={
              canStartCompSet
                ? "Open Comp Set editor on the right."
                : "Select at least 2 projects to build a Comp Set."
            }
          >
            Start Comp Set
          </button>
        </div>
      </div>

      {/* Project grid */}
      {loading && !projects.length ? (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-[11px] text-slate-300">
          {[...Array(3)].map((_, idx) => (
            <div
              key={idx}
              className="rounded-xl border border-slate-800 bg-slate-950/70 px-3 py-3 animate-pulse"
            >
              <div className="h-3 w-32 bg-slate-700/60 rounded mb-2" />
              <div className="h-3 w-24 bg-slate-700/40 rounded mb-1.5" />
              <div className="h-3 w-40 bg-slate-700/30 rounded" />
            </div>
          ))}
        </div>
      ) : total === 0 ? (
        <div className="mt-3 text-[11px] text-slate-400">
          No projects found for this configuration.
        </div>
      ) : (
        <>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-[11px] text-slate-300">
            {pageItems.map((p) => {
              const locationParts = [];
              if (p.state) locationParts.push(p.state);
              if (p.counties && p.counties.length) {
                locationParts.push(p.counties[0] + " Co.");
              }
              const location = locationParts.join(" · ");
              const cap =
                typeof p.capacity_mw === "number"
                  ? `${Number(p.capacity_mw).toLocaleString()} MW`
                  : "Capacity N/A";
              const selected = selectedIds.includes(p.id);
              const active = selectedProject?.id === p.id;

              return (
                <MiniProjectCard
                  key={p.id}
                  name={p.name || "Unnamed project"}
                  location={location || "Location TBD"}
                  capacity={cap}
                  note={
                    p.year ? `Online ${p.year}` : "Commissioning year TBD"
                  }
                  selected={selected}
                  active={active}
                  onToggle={() => onToggleSelected(p.id)}
                  onClick={() => onProjectClick(p)}
                />
              );
            })}
          </div>

          {/* Pagination */}
          <div className="mt-3 flex items-center justify-between text-[11px] text-slate-400">
            <div>
              Showing{" "}
              <span className="font-semibold">
                {total === 0
                  ? 0
                  : currentPage * PAGE_SIZE + 1}
                {"–"}
                {Math.min((currentPage + 1) * PAGE_SIZE, total)}
              </span>{" "}
              of <span className="font-semibold">{total}</span> projects
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handlePrev}
                disabled={currentPage === 0}
                className="rounded-lg border border-slate-700 bg-slate-950/80 px-2 py-1 disabled:opacity-40"
              >
                Prev
              </button>
              <span>
                Page{" "}
                <span className="font-semibold">
                  {currentPage + 1}
                </span>{" "}
                of <span className="font-semibold">{pageCount}</span>
              </span>
              <button
                type="button"
                onClick={handleNext}
                disabled={currentPage >= pageCount - 1}
                className="rounded-lg border border-slate-700 bg-slate-950/80 px-2 py-1 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
